import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminAnalytics, exportCSV } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, TrendingUp, Users, Ticket } from 'lucide-react';

export default function AdminAnalytics() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    async function load() {
      try { const res = await getAdminAnalytics(); setData(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [user, isAdmin, navigate, authLoading]);

  const handleExport = async (type) => {
    try {
      const res = await exportCSV(type);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `winluxor_${type}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); }
  };

  if (authLoading || !user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="font-display text-3xl font-bold tracking-tight">{t('analytics')}</h1><p className="text-muted-foreground mt-1 text-sm">Last 30 days</p></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2 rounded-xl" onClick={() => handleExport('users')}><Download className="w-4 h-4" /> Users CSV</Button>
            <Button variant="outline" size="sm" className="gap-2 rounded-xl" onClick={() => handleExport('transactions')}><Download className="w-4 h-4" /> Transactions CSV</Button>
            <Button variant="outline" size="sm" className="gap-2 rounded-xl" onClick={() => handleExport('entries')}><Download className="w-4 h-4" /> Entries CSV</Button>
          </div>
        </div>

        {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white border-border"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t('revenuePoints')} (30d)</p><p className="font-mono text-3xl font-bold text-primary mt-1">{data.totals?.revenue_30d?.toLocaleString() || 0}</p></div><TrendingUp className="w-8 h-8 text-primary opacity-30" /></div></CardContent></Card>
              <Card className="bg-white border-border"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">New Users (30d)</p><p className="font-mono text-3xl font-bold text-blue-600 mt-1">{data.totals?.new_users_30d?.toLocaleString() || 0}</p></div><Users className="w-8 h-8 text-blue-600 opacity-30" /></div></CardContent></Card>
              <Card className="bg-white border-border"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t('entries')} (30d)</p><p className="font-mono text-3xl font-bold text-green-600 mt-1">{data.totals?.entries_30d?.toLocaleString() || 0}</p></div><Ticket className="w-8 h-8 text-green-600 opacity-30" /></div></CardContent></Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-border"><CardHeader><CardTitle className="text-base">Revenue (Points Spent)</CardTitle></CardHeader><CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={data.revenue_chart}><CartesianGrid strokeDasharray="3 3" stroke="#e5e2d8" /><XAxis dataKey="date" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} /><Tooltip /><Area type="monotone" dataKey="revenue" stroke="#D4AF37" fill="#F6EED7" /></AreaChart>
                </ResponsiveContainer>
              </CardContent></Card>

              <Card className="bg-white border-border"><CardHeader><CardTitle className="text-base">New Users</CardTitle></CardHeader><CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={data.users_chart}><CartesianGrid strokeDasharray="3 3" stroke="#e5e2d8" /><XAxis dataKey="date" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} /><Tooltip /><Area type="monotone" dataKey="users" stroke="#3B82F6" fill="#DBEAFE" /></AreaChart>
                </ResponsiveContainer>
              </CardContent></Card>

              <Card className="bg-white border-border lg:col-span-2"><CardHeader><CardTitle className="text-base">Entries</CardTitle></CardHeader><CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={data.entries_chart}><CartesianGrid strokeDasharray="3 3" stroke="#e5e2d8" /><XAxis dataKey="date" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} /><Tooltip /><Area type="monotone" dataKey="entries" stroke="#16A34A" fill="#DCFCE7" /></AreaChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
